import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { organizationsAPI } from '../services/organizationsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import Button from '../components/UI/Button'
import { ArrowLeft, Save, X } from 'lucide-react'

export default function EditOrganization() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery(['organization', id], () => organizationsAPI.getById(id))
  const organization = data?.data?.data?.organization || null
  
  // Debug logging
  console.log('EditOrganization - API Response:', data)
  console.log('EditOrganization - Organization Data:', organization)

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  // Reset form when organization data loads
  useEffect(() => {
    if (organization) {
      reset({
        name: organization.name || '',
        email: organization.email || '',
        website: organization.website || '',
        industry: organization.industry || '',
        employee_count: organization.employee_count?.toString() || '',
        location: organization.location || '',
        country: organization.country || '',
        projects: organization.projects?.toString() || '',
        units: organization.units?.toString() || '',
        status: organization.status || 'active'
      })
    }
  }, [organization, reset])

  const mutation = useMutation(({ id, payload }) => organizationsAPI.update(id, payload), {
    onSuccess: (response) => {
      const updatedOrganization = response?.data?.data?.organization
      
      if (updatedOrganization) {
        // Update the organization detail cache immediately
        queryClient.setQueryData(['organization', id], {
          data: {
            data: {
              organization: updatedOrganization
            }
          }
        })
        
        // Update all organizations list cache entries (handles different query keys)
        queryClient.setQueriesData(['organizations'], (oldData) => {
          if (!oldData) return oldData
          
          // Handle the response structure: { data: { data: { organizations: [], pagination: {} } } }
          if (oldData?.data?.data?.organizations) {
            const { organizations, pagination } = oldData.data.data
            const updatedOrganizations = organizations.map(org => 
              org.id === id ? { ...org, ...updatedOrganization } : org
            )
            
            return {
              ...oldData,
              data: {
                ...oldData.data,
                data: {
                  organizations: updatedOrganizations,
                  pagination
                }
              }
            }
          }
          
          return oldData
        })
      }
      
      // Invalidate queries to ensure consistency and trigger refetch
      queryClient.invalidateQueries(['organization', id])
      queryClient.invalidateQueries(['organizations'])
      navigate(`/organizations/${id}`)
    }
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-secondary-900 mb-2">Organization not found</h2>
        <p className="text-secondary-600 mb-4">The organization you're trying to edit doesn't exist.</p>
        <Button onClick={() => navigate('/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    )
  }

  const handleFormSubmit = async (formData) => {
    // Convert string numbers to actual numbers
    const processedData = {
      ...formData,
      employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
      projects: formData.projects ? parseInt(formData.projects) : null,
      units: formData.units ? parseInt(formData.units) : null
    }
    
    mutation.mutate({ id, payload: processedData })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate(`/organizations/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Edit Organization</h1>
            <p className="text-secondary-600">{organization.name}</p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <Button variant="outline" onClick={() => navigate(`/organizations/${id}`)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Organization Name *
                </label>
                <input
                  {...register('name', { required: 'Organization name is required' })}
                  className={`input ${errors.name ? 'border-red-500' : ''}`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={`input ${errors.email ? 'border-red-500' : ''}`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Website
                </label>
                <input
                  {...register('website')}
                  placeholder="https://example.com"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Industry
                </label>
                <select {...register('industry')} className="input">
                  <option value="">Select Industry</option>
                  <option value="technology">Technology</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="education">Education</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="consulting">Consulting</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Employee Count
                </label>
                <input
                  type="text"
                  {...register('employee_count', { 
                    pattern: {
                      value: /^\d+$/,
                      message: 'Please enter a valid number'
                    }
                  })}
                  placeholder="Number of employees"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Location
                </label>
                <input
                  {...register('location')}
                  placeholder="City, State/Country"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Country
                </label>
                <input
                  {...register('country')}
                  placeholder="Country"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Number of Projects
                </label>
                <input
                  type="text"
                  {...register('projects', { 
                    pattern: {
                      value: /^\d+$/,
                      message: 'Please enter a valid number'
                    }
                  })}
                  placeholder="Number of projects"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Number of Units
                </label>
                <input
                  type="text"
                  {...register('units', { 
                    pattern: {
                      value: /^\d+$/,
                      message: 'Please enter a valid number'
                    }
                  })}
                  placeholder="Number of units"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Status
                </label>
                <select {...register('status')} className="input">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/organizations/${id}`)}
                disabled={mutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isLoading}
                className="min-w-[120px]"
              >
                {mutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}


